import { useEffect, useState } from "react";
import { Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch, Box, Button, makeStyles } from "@material-ui/core";
import DetailsDialog, { DetailsProps } from "../DetailsDialog";
import { Dimension, Identifiable, Price, areDimensionsEqual } from "../../model/Items";
import { CUBIC_METERS_IN_UNIT, CUBIC_METER_IN_CENTIMETERS } from "../../engine/Calculations";
import NumberInputField from "../ui/NumberInputField";
import TextInputField from "../ui/TextInputField";
import { incrementByPercent } from "../../engine/PriceUtilities";
import usePriceAdjustmentSuggestions from "../PriceAdjustmentSuggestions";
import { fetchFormPriceAdjustmentSuggestions } from "../../api/PriceAdjustmentSuggestionApi";
import FormActiveOrders from "./FormActiveOrders";
import { Material } from "../../api/MaterialApi";
import useFormApi from "../../api/FormApi";
import useMaterialPricingApi from "../../api/MaterialPricingApi.hook";

interface Props extends DetailsProps {
  readonly customerId: number;
  readonly priceListId: number;
}

interface Form {
  id?: number;
  name: string;
  dimension: Dimension;
  material: Identifiable;
  price?: Price;
  weight: number;
}

const useStyles = makeStyles(theme => ({
  button: {
    marginLeft: 'auto',
    marginTop: 'auto',
    marginBottom: 'auto',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  inputField: {
    minWidth: 280
  }
}));

const FormDetails = (props: Props) => {
  const materialPricingApi = useMaterialPricingApi();
  const formApi = useFormApi();
  const customerId = props.customerId;
  const priceListId = props.priceListId;
  const classes = useStyles();
  const priceAdjustmentSuggestions = usePriceAdjustmentSuggestions(fetchFormPriceAdjustmentSuggestions);
  const [materials, setMaterials] = useState<Material[]>();
  const [suggestedPriceAdjustment, setSuggestedPriceAdjustment] = useState<number>();
  const [hideNonPricingMaterials, setHideNonPricingMaterials] = useState<boolean>(true);
  const [userSelectedMaterial, setUserSelectedMaterial] = useState<Material|null>(null);
  const [initialMaterialId, setInitialMaterialId] = useState<number>();
  const [name, setName] = useState<string>();
  const [length, setLength] = useState<number>();
  const [width, setWidth] = useState<number>();
  const [depth, setDepth] = useState<number>();
  const [basePrice, setBasePrice] = useState<number|null>();
  const [priceAdjustment, setPriceAdjustment] = useState<number>();
  const [effectivePrice, setEffectivePrice] = useState<number|null>();
  const [weight, setWeight] = useState<number|null>();
  const [forceSaveOnValidationFailedMessage, setForceSaveOnValidationFailedMessage] = useState<string>();
  const [volume, setVolume] = useState<number|null>();

  const initializeFields = (data: Form) => {
    const dimension = data.dimension;
    const price = data.price;
    setUserSelectedMaterial(null);
    setInitialMaterialId(data.material.id);
    setName(data.name);
    setLength(dimension.length);
    setWidth(dimension.width);
    setDepth(dimension.depth);
    setBasePrice(price?.base);
    setPriceAdjustment(price?.adjustment);
    setEffectivePrice(price?.effective);
    setWeight(data.weight);
  };

  const collectData = () =>
    (name
      && userSelectedMaterial
      && length && width && depth
      && basePrice !== undefined && basePrice !== null
      && weight)
    ? {
      name,
      material: { id: userSelectedMaterial.id },
      dimension: { length, width, depth },
      price: { base: basePrice, adjustment: priceAdjustment, effective: null },
      weight
    }
    : null;

  const fetchMaterials = () => {
    materialPricingApi
      .getCustomerMaterialsWithPricingsApplied(customerId, priceListId, hideNonPricingMaterials)
      .then(materials => materials.sort((material, anotherMaterial) => material.name.localeCompare(anotherMaterial.name)))
      .then(materials => setMaterials(materials))
      .catch(reason => console.error(`Could not fetch materials: ${reason}`));
  };

  const calculateVolume = () => length && width && depth
    ? Number(length) * Number(width) * Number(depth) / CUBIC_METER_IN_CENTIMETERS
    : null;

  const calculateAndSetEffectivePrice = (basePrice: number) => {
    const effectivePrice = priceAdjustment
      ? incrementByPercent(basePrice, priceAdjustment)
      : basePrice;
    setEffectivePrice(effectivePrice);
  };

  const calculateAndSetPrices = (unitPrice: number, volume: number) => {
    const formBasePrice = unitPrice * volume / CUBIC_METERS_IN_UNIT;
    if (formBasePrice) {
      const roundedFormBasePrice = Number(formBasePrice.toFixed(2));
      setBasePrice(roundedFormBasePrice);
      calculateAndSetEffectivePrice(roundedFormBasePrice);
    }
  };

  const calculateAndSetWeight = (density: number, volume: number) => {
    const formWeight = density * volume;
    if (formWeight) {
      const weight = Number(formWeight.toFixed(3));
      setWeight(weight === 0 ? Number(formWeight.toFixed(4)) : weight);
    }
  };

  const handleMaterialChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedMaterial = event.target.value as Material;
    setUserSelectedMaterial(selectedMaterial);
  };

  const isNotGreaterThanZero = (value: number|null|undefined) =>
    value !== undefined && value !== null && value <= 0;

  const reportAlert = (message: string) => props.reportAlert && props.reportAlert(message);

  const afterFormSave = (responseData: object, closeDialog: boolean) => {
    props.afterSave(responseData, closeDialog);
    const responseCalculatedEffectivePrice = (responseData as Form).price?.effective;
    console.debug(`### onFormSave: ${effectivePrice} : ${responseCalculatedEffectivePrice}`);
    if (effectivePrice !== responseCalculatedEffectivePrice) {
      reportAlert(`
        Usługa zmodyfikowała zaprezentowaną wcześniej cenę dla klienta
        z ${effectivePrice} na ${responseCalculatedEffectivePrice}`);
    }
  };

  const validateForm = async (form: Form) => {
    const forms = await formApi.fetchForms(customerId, priceListId);
    const formWithSameDimension = forms
      .filter(fetchedForm => props.instanceId !== fetchedForm.id)
      .find(fetchedForm => form.material.id === fetchedForm.material.id && areDimensionsEqual(form.dimension, fetchedForm.dimension));
    const duplicateFound = formWithSameDimension !== undefined;
    const message = duplicateFound
      ? `Znaleziono formatkę z tego samego materiału z identycznymi wymiarami: ${formWithSameDimension?.name}`
      : undefined;
    setForceSaveOnValidationFailedMessage(message);
    return !duplicateFound;
  };

  const handleHideNonPricingMaterialsChecked
    = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setHideNonPricingMaterials(checked);

  const initializeSuggestedPriceAdjustment = (volume: number) => {
    const suggestedPriceAdjustment = priceAdjustmentSuggestions.findPriceAdjutmentSuggestionForVolume(volume);
    setSuggestedPriceAdjustment(suggestedPriceAdjustment);
  };

  const handleApplySuggestedPriceAdjustment = () => setPriceAdjustment(suggestedPriceAdjustment);

  useEffect(fetchMaterials, [hideNonPricingMaterials]);

  useEffect(() => {
    if (volume !== undefined && volume !== null) {
      initializeSuggestedPriceAdjustment(volume)
    }
  }, [suggestedPriceAdjustment]);

  useEffect(() => {
    console.debug(`### base price recalculation`);
    if (userSelectedMaterial) {
      const volume = calculateVolume();
      setVolume(volume);
      console.debug(`### base price recalculation / volume: ${volume}`);
      if (volume === null) {
        console.debug(`### base price recalculation / clearing base price`);
        setBasePrice(null);
        setEffectivePrice(null);
        setWeight(null);
      } else {
        calculateAndSetPrices(userSelectedMaterial.unitPrice, volume);
        calculateAndSetWeight(userSelectedMaterial.density, volume);
        initializeSuggestedPriceAdjustment(volume);
      }
    }
  }, [userSelectedMaterial, length, width, depth]);

  useEffect(() => {
    console.debug(`### effective price recalculation`);
    if (basePrice === null || basePrice === undefined) {
      setEffectivePrice(null);
    } else {
      calculateAndSetEffectivePrice(basePrice);
    }
  }, [basePrice, priceAdjustment]);
 
  useEffect(() => {
    if (materials && initialMaterialId) {
      const selectedMaterial = materials.find(item => item.id === initialMaterialId);
      setUserSelectedMaterial(selectedMaterial || null);
    }
  }, [materials, initialMaterialId]);

  const renderFields = (initialData: Form|undefined) => (
    <>
      <h2>Wprowadź dane formatki</h2>
      <FormControlLabel
        label="Ukryj materiały spoza cennika"
        control={
          <Switch
            color='primary'
            defaultChecked
            checked={hideNonPricingMaterials}
            onChange={handleHideNonPricingMaterialsChecked}
            size='small'
          />}
      />
      {materials && <FormControl>
        <InputLabel>Materiał</InputLabel>
        <Select value={userSelectedMaterial || ''} onChange={handleMaterialChange}>
          {materials.map(material => (<MenuItem key={material.id} value={material as any}>{material.name}</MenuItem>))}
        </Select>
      </FormControl>}
      <TextInputField
        label='Nazwa'
        autoFocus
        initialValue={initialData?.name}
        errorMessage={name => !name ? "Nazwa jest wymagana" : null}
        onValueChange={setName}
      />
      <NumberInputField
        label='Długość'
        initialValue={initialData?.dimension.length}
        onValueChange={setLength}
      />
      <NumberInputField
        label='Szerokość'
        initialValue={initialData?.dimension.width}
        onValueChange={setWidth}
      />
      <NumberInputField
        label='Wysokość'
        initialValue={initialData?.dimension.depth}
        onValueChange={setDepth}
      />
      <NumberInputField
        label='Objętość [m3]'
        initialValue={volume === null ? undefined : volume}
        disabled
      />
      <NumberInputField
        label='Cena bazowa'
        initialValue={initialData?.price?.base}
        calculatedValue={basePrice}
        errorMessage={() => isNotGreaterThanZero(basePrice) ? "Wyliczona cena bazowa jest zbyt niska, zwiększ wymiary" : null}
        disabled
      />
      <NumberInputField
        label='Dodatek do ceny [%]'
        initialValue={initialData?.price?.adjustment}
        calculatedValue={ priceAdjustment }
        onValueChange={setPriceAdjustment}
      />
      { suggestedPriceAdjustment !== priceAdjustment && <Box display='flex' flexDirection='row'>
        <NumberInputField
          classes={ classes }
          label='Sugerowany dodatek do ceny [%]'
          initialValue={ suggestedPriceAdjustment }
          calculatedValue={ suggestedPriceAdjustment }
          disabled
        />
        <Button
          color='primary'
          className={ classes.button }
          onClick={ handleApplySuggestedPriceAdjustment }
          variant='contained'>
            Przepisz sugerowany dodatek
        </Button>
      </Box> }
      <NumberInputField
        label='Cena dla klienta'
        initialValue={initialData?.price?.effective || undefined}
        calculatedValue={effectivePrice}
        errorMessage={() => isNotGreaterThanZero(effectivePrice) ? "Wyliczona cena jest zbyt niska, zwiększ wymiary" : null}
        disabled
      />
      <NumberInputField
        label='Waga'
        initialValue={initialData?.weight}
        calculatedValue={weight}
        errorMessage={() => isNotGreaterThanZero(weight) ? "Wyliczona waga jest zbyt niska, zwiększ wymiary" : null}
        onValueChange={setWeight}
      />
      { props.instanceId && <FormActiveOrders formId={ props.instanceId } /> }
    </>
  );

  return (
    <>
      <DetailsDialog<Form>
        { ...props }
        resource={`customers/${customerId}/price-lists/${priceListId}/forms`}
        renderFields={renderFields}
        initializeFields={initializeFields}
        collectData={collectData}
        afterSave={afterFormSave}
        validateData={validateForm}
        forceSaveOnValidationFailedMessage={forceSaveOnValidationFailedMessage}
      />
    </>
  );
};
export default FormDetails;